import { Divider, Tab, Tabs, Theme, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  Datagrid,
  Filter,
  FilterProps,
  Identifier,
  List,
  ListContextProvider,
  ListProps,
  TextField,
  TextInput,
  useGetList,
  useListContext,
} from "react-admin";
import { CustomDateField } from "../../../Components/CustomDateField";
import { CustomDatetime } from "../../../Components/CustomDatetime";
import { DetailButton } from "../../../Components/DetailButton";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { DefaultCreatedDateSort, DefaultIsActiveFilter, DefaultPerPageSize } from "../../../Constants/Constant";
import { ActivePassiveStatus } from "../../../Enums/Active";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { AirportExcelHeader, AirportExcelName } from "./Constant/Excel";
import { SourceAirport, TitleAirport } from "./Constant/Source";

const useDatagridStyles = makeStyles({
  total: { fontWeight: "bold" },
});

const AirportFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <TextInput label="Airport Code" source="airportCode__contains" alwaysOn />
    <TextInput label="AirportName Name" source="airportName__contains" />
    <TextInput label="Three Digit Airport Code" source="threeDigitAirportCode__contains" alwaysOn />
    <CustomDatetime source="createdDate__gte" label="Tarih(>=)" />
    <CustomDatetime source="createdDate__lte" label="Tarih (<=)" />
  </Filter>
);

const ExportToExcel = data => {
  ExcelExport(
    AirportExcelHeader,
    data.map(v => [v.airportCode, v.airportName, v.threeDigitAirportCode, v.createdBy, v.createdDate]),
    AirportExcelName
  );
};

const useGetTotals = (filterValues: any) => {
  const { total: totalIsActive } = useGetList(
    SourceAirport,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: true }
  );
  const { total: totalIsPassive } = useGetList(
    SourceAirport,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: false }
  );

  return {
    active: totalIsActive,
    passive: totalIsPassive,
  };
};

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down("xs"));
  const [isActive, setIsActive] = useState<Identifier[]>([] as Identifier[]);
  const [isPassive, setIsPassive] = useState<Identifier[]>([] as Identifier[]);
  const classes = useDatagridStyles();
  const totals = useGetTotals(filterValues) as any;

  useEffect(() => {
    if (ids && ids !== filterValues.isActive__eq) {
      switch (filterValues.isActive__eq) {
        case true:
          setIsActive(ids);
          break;
        case false:
          setIsPassive(ids);
          break;
      }
    }
  }, [ids, filterValues.isActive__eq]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, value: any) => {
      setFilters && setFilters({ ...filterValues, isActive__eq: value == 1 }, displayedFilters);
    },
    [displayedFilters, filterValues, setFilters]
  );

  const selectedIds = filterValues.isActive__eq == true ? isActive : filterValues.isActive__eq == false;

  return (
    <Fragment>
      <Tabs
        variant="fullWidth"
        centered
        value={filterValues.isActive__eq ? 1 : 0}
        indicatorColor="primary"
        onChange={handleChange}
      >
        {ActivePassiveStatus.map(choice => (
          <Tab
            key={choice.id}
            label={totals[choice.name] ? `${choice.name} (${totals[choice.name]})` : choice.name}
            value={choice.id}
          />
        ))}
      </Tabs>
      <Divider />
      {isXSmall ? (
        <ListContextProvider value={{ ...listContext, ids: selectedIds }}>
          <></>
        </ListContextProvider>
      ) : (
        <ListContextProvider
          value={{
            ...listContext,
            ids: filterValues.isActive_eq ? isActive : isPassive,
          }}
        >
          <Datagrid {...props} optimized hasBulkActions={false}>
            <DetailButton label="Actions" resource={SourceAirport} />
            <TextField source="airportName" label="AIRPORT NAME" />
            <TextField source="airportCode" label="AIRPORT CODE" />
            <TextField source="threeDigitAirportCode" label=" THREE DIGIT AIRPORT CODE" />
            <CreatedByField label="CREATED BY" link={false} />
            <CustomDateField source="createdDate" label="CREATED DATE" showTime />
          </Datagrid>
        </ListContextProvider>
      )}
    </Fragment>
  );
};

const AirportList: FC<ListProps> = props => (
  <List
    {...props}
    title={TitleAirport.list}
    filterDefaultValues={DefaultIsActiveFilter}
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    exporter={ExportToExcel}
    filters={<AirportFilter />}
  >
    <TabbedDatagrid />
  </List>
);

export default AirportList;
